import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PerformanceDeltaLabel } from '~/modules/artist-roster/components/PerformanceDeltaLabel/PerformanceDeltaLabel';
import { SortableIndicator } from '~/modules/artist-roster/components/Table/components/SortableIndicator/SortableIndicator';
import { RangeIndicator } from '~/modules/artist-roster/pages/artist-details/pages/artist-tracks/components/TrackList/components/RangeIndicator/RangeIndicator';
import type { AssetFamily } from '~/modules/artist-roster/types';
import { formatMillionsNumber } from '~/modules/artist-roster/utils/formatMillionsNumber';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

import { TimeRangeLabel } from './TimeRangeLabel';
import { TrackTableLinkMenu } from './TrackTableLinkMenu';
import { TrackTableParticipantsCell } from './TrackTableParticipantsCell/TrackTableParticipantsCell';
import { TrackTableTitleCell } from './TrackTableTitleCell';
import { formatDateByLocale, getBenchmarkComparisonPercentage } from './utils';

type TimeRange = 'TW' | 'TM' | 'TY';

export function useTrackTableColumns({ range }: { range: TimeRange }) {
  const { t } = useTranslation('roster');
  const today = useMemo(() => new Date(), []);

  const [isAssetPerformanceEnabled, isTrackPreReleasesEnabled] =
    useFeatureFlags([
      FEATURE_FLAGS.WmgAssetPerformance,
      FEATURE_FLAGS.WmgTrackPreReleases,
    ]);

  return useMemo(() => {
    const columnHelper = createColumnHelper<AssetFamily>();

    const formatMetric = (
      row: AssetFamily,
      metricNumber: number | undefined,
    ) => {
      const releaseDate = row.initialReleaseDate
        ? new Date(row.initialReleaseDate)
        : null;

      if (
        releaseDate &&
        releaseDate < today &&
        (!!metricNumber || metricNumber === 0) &&
        Number.isFinite(metricNumber)
      ) {
        return formatMillionsNumber(metricNumber, true);
      }

      return null;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const trackTableColumns: ColumnDef<AssetFamily, any>[] = [
      columnHelper.accessor('title', {
        id: 'title',
        enableSorting: true,
        header: (header) => {
          return (
            <div className="flex items-center gap-sm">
              <span>{t('trackList.header.trackTitle')}</span>
              <SortableIndicator
                column={header.column}
                tooltip={t('trackList.tooltips.sortBy', {
                  field: t('trackList.header.trackTitle'),
                })}
              />
            </div>
          );
        },
        cell: ({ row }) => <TrackTableTitleCell asset={row.original} />,
      }),
      columnHelper.accessor('initialReleaseDate', {
        id: 'initialReleaseDate',
        enableSorting: true,
        meta: {
          className: 'w-[12%] min-w-[200px]',
        },
        header: (header) => {
          return (
            <div className="flex items-center gap-sm">
              <span>{t('trackList.header.releaseDate')}</span>
              <SortableIndicator
                column={header.column}
                tooltip={t('trackList.tooltips.sortBy', {
                  field: t('trackList.header.releaseDate'),
                })}
              />
            </div>
          );
        },
        cell: ({ row }) => (
          <div className="p-sm">
            {row.original.initialReleaseDate ? (
              formatDateByLocale(row.original.initialReleaseDate)
            ) : (
              <span className="px-sm py-md pl-xl text-subtle">--</span>
            )}
          </div>
        ),
      }),
    ];

    // Only create performance columns if feature is enabled
    if (isAssetPerformanceEnabled) {
      trackTableColumns.push(
        columnHelper.accessor('performance.streaming.aggregated.totalStreams', {
          id: 'streamsRTD',
          enableSorting: false,
          header: () => (
            <div className="flex items-end gap-xs text-nowrap">
              <span>{t('trackList.header.streams')}</span>
              <TimeRangeLabel timeRange="RTD" />
            </div>
          ),
          cell: ({ row }) => {
            const totalStreamsValue =
              row.original.performance?.streaming?.aggregated?.totalStreams;
            const benchmarkValue = row.original.performance?.streamingBenchmark;

            const totalStreams = formatMetric(row.original, totalStreamsValue);

            if (!totalStreams) {
              return <span className="px-sm py-md pl-md text-subtle">--</span>;
            }

            const benchmarkComparisonPercent =
              totalStreamsValue && benchmarkValue
                ? getBenchmarkComparisonPercentage(
                    totalStreamsValue,
                    benchmarkValue,
                  )
                : 0;

            const showBenchmarkComparison =
              isTrackPreReleasesEnabled && totalStreamsValue && benchmarkValue;

            return showBenchmarkComparison ? (
              <div>
                <div className="px-sm">{totalStreams}</div>

                <RangeIndicator
                  percent={benchmarkComparisonPercent}
                  isDefault={true}
                />
              </div>
            ) : (
              <span className="p-sm">{totalStreams}</span>
            );
          },
        }),
        columnHelper.accessor(
          'performance.streaming.aggregated.periodStreams.current',
          {
            id: 'streams',
            enableSorting: false,
            header: () => (
              <div className="flex items-end gap-xs text-nowrap">
                <span>{t('trackList.header.streams')}</span>
                <TimeRangeLabel timeRange={range} />
              </div>
            ),
            cell: ({ row }) => {
              const periodStreams = formatMetric(
                row.original,
                row.original.performance?.streaming?.aggregated?.periodStreams
                  ?.current,
              );

              return periodStreams ? (
                <div className="flex flex-col px-sm">
                  <span>{periodStreams}</span>
                  <PerformanceDeltaLabel
                    delta={
                      row.original.performance?.streaming?.aggregated
                        ?.periodStreams?.deltaRatio
                    }
                  />
                </div>
              ) : (
                <span className="px-sm py-md pl-md text-subtle">--</span>
              );
            },
          },
        ),
        columnHelper.accessor(
          'performance.streaming.aggregated.organicStreams.current',
          {
            id: 'organicStreams',
            enableSorting: false,
            header: () => (
              <div className="flex items-end gap-xs text-nowrap">
                <span>{t('trackList.header.organicStreams')}</span>
                <TimeRangeLabel timeRange={range} />
              </div>
            ),
            cell: ({ row }) => {
              const organicStreams = formatMetric(
                row.original,
                row.original.performance?.streaming?.aggregated?.organicStreams
                  ?.current,
              );

              return organicStreams ? (
                <div className="flex flex-col px-sm">
                  <span>{organicStreams}</span>
                  <PerformanceDeltaLabel
                    delta={
                      row.original.performance?.streaming?.aggregated
                        ?.organicStreams?.deltaRatio
                    }
                  />
                </div>
              ) : (
                <span className="px-sm py-md pl-md text-subtle">--</span>
              );
            },
          },
        ),
        columnHelper.accessor(
          'performance.engagement.aggregated.socialCreations.current',
          {
            id: 'socialCreations',
            enableSorting: false,
            header: () => (
              <div className="flex items-end gap-xs text-nowrap">
                <span>{t('trackList.header.socialCreations')}</span>
                <TimeRangeLabel timeRange="TW" />
              </div>
            ),
            cell: ({ row }) => {
              const socialCreations = formatMetric(
                row.original,
                row.original.performance?.engagement?.aggregated
                  ?.socialCreations?.current,
              );

              return socialCreations ? (
                <div className="flex flex-col px-sm">
                  <span>{socialCreations}</span>
                  <PerformanceDeltaLabel
                    delta={
                      row.original.performance?.engagement?.aggregated
                        ?.socialCreations?.deltaRatio
                    }
                  />
                </div>
              ) : (
                <span className="px-sm py-md pl-md text-subtle">--</span>
              );
            },
          },
        ),
        columnHelper.accessor('contributors', {
          id: 'contributors',
          header: (header) => (
            <div className="flex items-center gap-xs">
              <span>{t('trackList.header.participants')}</span>
              <SortableIndicator
                column={header.column}
                tooltip={t('trackList.tooltips.sortBy', {
                  field: t('trackList.header.participants'),
                })}
              />
            </div>
          ),
          cell: ({ row }) => (
            <TrackTableParticipantsCell asset={row.original} />
          ),
        }),
        columnHelper.display({
          id: 'additionalMenu',
          enableSorting: false,
          meta: {
            className: 'w-3xl',
          },
          header: () => null,
          cell: ({ row }) => <TrackTableLinkMenu trackData={row.original} />,
        }),
      );
    }

    return trackTableColumns;
  }, [t, today, range, isAssetPerformanceEnabled, isTrackPreReleasesEnabled]);
}
